import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radio, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Radio className="h-5 w-5" /></div>
          <span className="font-heading text-xl font-bold">CommConnect</span>
        </div>
        {done ? (
          <div className="space-y-4" data-testid="reset-success">
            <div className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-6 w-6" /> <span className="font-heading text-lg font-semibold">Password updated</span></div>
            <p className="text-sm text-muted-foreground">Your password has been reset. You can now sign in.</p>
            <Button className="w-full rounded-full" onClick={() => navigate("/AEGO")} data-testid="reset-to-login">Go to sign in</Button>
          </div>
        ) : !token ? (
          <p className="text-sm text-destructive">This reset link is missing its token. Please use the link from your email.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h2 className="font-heading text-2xl font-bold">Set a new password</h2>
            <div>
              <Label htmlFor="pw">New password</Label>
              <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" data-testid="reset-pw" required />
            </div>
            <div>
              <Label htmlFor="pw2">Confirm password</Label>
              <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5" data-testid="reset-pw2" required />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="reset-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
